/**
 * Home Page Community Showcase
 * Editorial horizontal gallery: scroll / drag / arrows / keyboard.
 * No autoplay. Videos muted+loop, pause when off-screen.
 */
(function () {
  'use strict';

  if (customElements.get('yugen-home-page-community-showcase')) return;

  class YugenHomePageCommunityShowcase extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.isDragging = false;
      this.dragMoved = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-hpcs-track]');
      this.panels = Array.from(this.querySelectorAll('[data-hpcs-panel]'));
      this.prevBtn = this.querySelector('[data-hpcs-prev]');
      this.nextBtn = this.querySelector('[data-hpcs-next]');
      this.currentEl = this.querySelector('[data-hpcs-current]');
      this.totalEl = this.querySelector('[data-hpcs-total]');
      this.progressFill = this.querySelector('[data-hpcs-progress]');
      this.videos = Array.from(this.querySelectorAll('[data-hpcs-video]'));

      if (!this.track || this.panels.length === 0) return;

      if (this.totalEl) {
        this.totalEl.textContent = String(this.panels.length).padStart(2, '0');
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('click', this.onClickCapture, true);
      window.addEventListener('resize', this.onResize);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.setAttribute('tabindex', '0');
      this.setupVideos();
      this.updateFromScroll();
    }

    disconnectedCallback() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('click', this.onClickCapture, true);
      }
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.teardownDrag();
      if (this.videoObserver) this.videoObserver.disconnect();
      this.videos.forEach(function (video) {
        video.pause();
      });
    }

    setupVideos() {
      var reduce =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.videos.forEach(function (video) {
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        if (reduce) {
          video.removeAttribute('autoplay');
          video.pause();
        }
      });

      if (!('IntersectionObserver' in window)) return;

      this.videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (reduce) {
              video.pause();
              return;
            }
            if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
              var playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(function () {});
              }
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.45, 0.7] }
      );

      this.videos.forEach(
        function (video) {
          this.videoObserver.observe(video);
        }.bind(this)
      );
    }

    onPrev() {
      this.goTo(this.index - 1);
    }

    onNext() {
      this.goTo(this.index + 1);
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.onPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.onNext();
      }
    }

    onBlockSelect(event) {
      var panel = event.target.closest('[data-hpcs-panel]');
      var i = panel ? this.panels.indexOf(panel) : -1;
      if (i !== -1) this.goTo(i, { instant: true });
    }

    onResize() {
      this.goTo(this.index, { instant: true });
    }

    onScroll() {
      window.requestAnimationFrame(
        function () {
          this.updateFromScroll();
        }.bind(this)
      );
    }

    onPointerDown(event) {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      this.isDragging = true;
      this.dragMoved = false;
      this.startX = event.clientX;
      this.scrollLeftStart = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;
      var dx = event.clientX - this.startX;
      if (Math.abs(dx) > 4) this.dragMoved = true;
      this.track.scrollLeft = this.scrollLeftStart - dx;
    }

    onPointerUp(event) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch (e) {}
      this.teardownDrag();
      this.snapNearest();
    }

    teardownDrag() {
      if (!this.track) return;
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
    }

    onClickCapture(event) {
      if (this.dragMoved) {
        event.preventDefault();
        event.stopPropagation();
        this.dragMoved = false;
      }
    }

    snapNearest() {
      var nearest = this.getNearestIndex();
      this.goTo(nearest);
    }

    getNearestIndex() {
      var trackRect = this.track.getBoundingClientRect();
      var pad = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      var origin = trackRect.left + pad;
      var nearest = 0;
      var best = Infinity;

      this.panels.forEach(
        function (panel, i) {
          var rect = panel.getBoundingClientRect();
          var dist = Math.abs(rect.left - origin);
          if (dist < best) {
            best = dist;
            nearest = i;
          }
        }.bind(this)
      );

      return nearest;
    }

    goTo(next, options) {
      var max = this.panels.length - 1;
      var i = Math.max(0, Math.min(next, max));
      var panel = this.panels[i];
      if (!panel || !this.track) return;

      this.index = i;
      var behavior =
        options && options.instant
          ? 'auto'
          : window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';

      var trackRect = this.track.getBoundingClientRect();
      var panelRect = panel.getBoundingClientRect();
      var pad = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      var delta = panelRect.left - (trackRect.left + pad);
      var target = this.track.scrollLeft + delta;

      this.track.scrollTo({ left: target, behavior: behavior });
      this.updateUI(i);
    }

    updateFromScroll() {
      var nearest = this.getNearestIndex();
      if (nearest !== this.index) this.index = nearest;
      this.updateUI(this.index);
    }

    updateUI(i) {
      this.panels.forEach(function (panel, idx) {
        var active = idx === i;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.currentEl) {
        this.currentEl.textContent = String(i + 1).padStart(2, '0');
      }

      if (this.progressFill && this.panels.length > 1) {
        var pct = ((i + 1) / this.panels.length) * 100;
        this.progressFill.style.width = pct + '%';
      }

      if (this.prevBtn) this.prevBtn.disabled = i === 0;
      if (this.nextBtn) this.nextBtn.disabled = i === this.panels.length - 1;
    }
  }

  customElements.define(
    'yugen-home-page-community-showcase',
    YugenHomePageCommunityShowcase
  );
})();
